/**
 * #939 3c — the client side of assign / move / undo (pure, no React, no I/O).
 *
 * Ted's regulatory rule: *"dogs from different families cannot actually be
 * housed together unsupervised … the system should never suggest that two
 * unrelated dogs can share a sleeping space simply to increase capacity."*
 * The SERVER refuses an unrelated pairing (409 `unrelated_family`); this
 * module makes sure the UI never OFFERS one — a bed the desk can drop a stay
 * onto is a bed the rule would accept, judged from the stays on screen.
 * (Nights outside the visible range are the server's call; a refusal there
 * still reads as a plain sentence naming the dog.)
 */
import type { LodgingStay, LodgingUnassignedBoarder, LodgingUnit } from './api.js';

/** A stay or a boarder the desk wants to place: its family, dogs and nights. */
export interface Placement {
  /** The assignment being moved (null for a boarder with no bed yet). */
  assignmentId: string | null;
  bookingId: string;
  customerId: string | null;
  dogCount: number;
  /** First night, YYYY-MM-DD. */
  startDate: string;
  /** Checkout day (exclusive end), YYYY-MM-DD. */
  endDate: string;
}

export function placementOfStay(s: LodgingStay): Placement {
  return { assignmentId: s.assignmentId, bookingId: s.bookingId, customerId: s.customerId, dogCount: s.dogCount, startDate: s.startDate, endDate: s.endDate };
}

/** A boarder's Booking dates are inclusive; the bed row's end is the checkout day — the same VALUE. */
export function placementOfBoarder(b: LodgingUnassignedBoarder): Placement {
  return { assignmentId: null, bookingId: b.bookingId, customerId: b.customerId, dogCount: b.dogCount, startDate: b.startDate, endDate: b.endDate };
}

function spansOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart < bEnd && bStart < aEnd;
}

export type BedVerdict =
  | { ok: true }
  | { ok: false; reason: 'retired' | 'taken' | 'unrelated_family' | 'full'; dogNames: string[] };

/**
 * Could `p` sleep on `unit` for its nights, judged from the stays on screen?
 *   · an exclusive bed (capacity 1) must be free of any other overlapping stay;
 *   · a shared bed takes only the SAME family, and only while the dogs fit;
 *   · the stay being moved never blocks itself.
 */
export function bedVerdict(unit: LodgingUnit, p: Placement, stays: ReadonlyArray<LodgingStay>): BedVerdict {
  if (!unit.isActive) return { ok: false, reason: 'retired', dogNames: [] };
  const others = stays.filter(
    (s) => s.resourceUnitId === unit.id && s.assignmentId !== p.assignmentId && spansOverlap(p.startDate, p.endDate, s.startDate, s.endDate),
  );
  if (others.length === 0) return { ok: true };
  if (unit.capacity <= 1) return { ok: false, reason: 'taken', dogNames: others.flatMap((s) => s.dogNames) };
  const stranger = others.find((s) => s.customerId === null || p.customerId === null || s.customerId !== p.customerId);
  if (stranger) return { ok: false, reason: 'unrelated_family', dogNames: stranger.dogNames };
  const dogs = others.reduce((n, s) => n + s.dogCount, 0) + p.dogCount;
  if (dogs > unit.capacity) return { ok: false, reason: 'full', dogNames: others.flatMap((s) => s.dogNames) };
  return { ok: true };
}

/** The beds the UI may offer for `p` — the rule is never suggested around. */
export function offerableBeds(units: ReadonlyArray<LodgingUnit>, p: Placement, stays: ReadonlyArray<LodgingStay>): LodgingUnit[] {
  return units.filter((u) => bedVerdict(u, p, stays).ok);
}

/**
 * Ted #35 (his Item 33) — what a bed picker shows, and why.
 *
 * Ted, 10-06: *"I'm also getting 'no bed is free these nights,' even though I
 * set the capacity at 15."* He had set the THRESHOLD (15) and had ZERO beds;
 * the picker read "no bed is free" — a full house — when the truth was that
 * no bed existed yet. The threshold is deliberately not the inventory
 * (`lodging-schema.ts`), so the picker must tell the two apart:
 *   · no ACTIVE bed at all → `no-beds` (say where beds are set up);
 *   · beds exist, none the rule would accept → `none-free` (`moving` is true
 *     when an assigned stay is being moved, so the copy can say "other");
 *   · otherwise the beds to offer.
 */
export type BedPickerState =
  | { kind: 'no-beds' }
  | { kind: 'none-free'; moving: boolean }
  | { kind: 'offer'; beds: LodgingUnit[] };

export function bedPickerState(
  units: ReadonlyArray<LodgingUnit>,
  p: Placement,
  stays: ReadonlyArray<LodgingStay>,
  excludeUnitId?: string,
): BedPickerState {
  if (!units.some((u) => u.isActive)) return { kind: 'no-beds' };
  const beds = offerableBeds(units, p, stays).filter((u) => u.id !== excludeUnitId);
  if (beds.length === 0) return { kind: 'none-free', moving: excludeUnitId !== undefined };
  return { kind: 'offer', beds };
}

// ── The change record and its inverse (Undo issues the reverse call) ──────

export type LodgingChange =
  | { kind: 'assigned'; assignmentId: string; bookingId: string; dogNames: string[]; unitId: string; unitName: string; startDate: string; endDate: string }
  | {
      kind: 'moved';
      assignmentId: string;
      bookingId: string;
      dogNames: string[];
      from: { unitId: string; unitName: string; startDate: string; endDate: string };
      to: { unitId: string; unitName: string; startDate: string; endDate: string };
    }
  | { kind: 'unassigned'; bookingId: string; dogNames: string[]; from: { unitId: string; unitName: string; startDate: string; endDate: string } };

export type ReverseCall =
  | { method: 'DELETE'; assignmentId: string }
  | { method: 'PATCH'; replaceAssignmentId: string; bookingId: string; unitId: string; startDate: string; endDate: string }
  | { method: 'POST'; bookingId: string; unitId: string; startDate: string; endDate: string };

/** What Undo sends: an assign is undone by a DELETE, a move by a PATCH back, an unassign by a POST back. */
export function reverseOf(change: LodgingChange): ReverseCall {
  switch (change.kind) {
    case 'assigned':
      return { method: 'DELETE', assignmentId: change.assignmentId };
    case 'moved':
      return { method: 'PATCH', replaceAssignmentId: change.assignmentId, bookingId: change.bookingId, unitId: change.from.unitId, startDate: change.from.startDate, endDate: change.from.endDate };
    case 'unassigned':
      return { method: 'POST', bookingId: change.bookingId, unitId: change.from.unitId, startDate: change.from.startDate, endDate: change.from.endDate };
  }
}

export function describeChange(change: LodgingChange): string {
  const dogs = change.dogNames.join(' & ') || 'The dog';
  switch (change.kind) {
    case 'assigned':
      return `${dogs} → ${change.unitName}.`;
    case 'moved':
      return change.from.unitId === change.to.unitId
        ? `${dogs} moved on ${change.to.unitName} to start ${change.to.startDate}.`
        : `${dogs} moved from ${change.from.unitName} to ${change.to.unitName}.`;
    case 'unassigned':
      return `${dogs} taken off ${change.from.unitName}.`;
  }
}

// Sawyer 06 beds-lockers-runcard (B2) — a bed's option text everywhere a bed
// is picked: "Dog 4 · Big (up to 2 dogs)". The seam lane 07's booking bed
// picker switches to once both land. No size ⇒ no size text, never "None".
export function bedOptionLabel(unit: { name: string; capacity: number; sizeClass?: string | null }): string {
  const size = unit.sizeClass ? ` · ${unit.sizeClass}` : '';
  const shared = unit.capacity > 1 ? ` (up to ${unit.capacity} dogs)` : '';
  return `${unit.name}${size}${shared}`;
}
